import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";

/**
 * Piezas de interfaz reutilizables. Se mantienen pequeñas y sin estado
 * propio: la lógica vive en `src/lib` (pura y testeable) y los datos en
 * TanStack Query. Aquí solo hay presentación.
 */

export type Tone = "ok" | "warn" | "danger" | "neutral";

export function Button({
  variant = "quiet",
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "quiet" | "danger" }) {
  const className =
    variant === "primary" ? "btn btn-primary" : variant === "danger" ? "btn btn-danger" : "btn btn-quiet";
  return (
    <button type="button" className={className} {...rest}>
      {children}
    </button>
  );
}

export function Chip({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  const map: Record<Tone, string> = {
    ok: "chip chip-ok",
    warn: "chip chip-warn",
    danger: "chip chip-danger",
    neutral: "chip chip-neutral",
  };
  return <span className={map[tone]}>{children}</span>;
}

/**
 * El sello: se usa **una vez** por pantalla, en la ficha del documento, para
 * señalar su estado normativo. Es el único elemento expresivo del panel; el
 * resto es deliberadamente sobrio porque esto es una herramienta de trabajo
 * diario, no una página de presentación.
 */
export function StatusStamp({ status }: { status: string }) {
  const tone =
    status === "ACTIVO"
      ? "border-signal-ok text-signal-ok dark:text-teal-300"
      : status === "DESCARGA_DESACTIVADA"
        ? "border-signal-warn text-signal-warn dark:text-amber-300"
        : "border-ink-400 text-ink-500 dark:border-ink-600 dark:text-ink-400";
  const label =
    status === "ACTIVO"
      ? "Activo"
      : status === "DESCARGA_DESACTIVADA"
        ? "Descarga desactivada"
        : status === "SUSTITUIDO"
          ? "Sustituido"
          : status;
  return <span className={`stamp ${tone}`}>{label}</span>;
}

/**
 * Placa ADR: el rombo naranja de la señalización real de mercancías
 * peligrosas. No es adorno — marca los envíos que llevan bloque ADR, que es
 * la excepción que hay que ver de un vistazo en la tabla.
 */
export function AdrPlacard({ title = "Envío con mercancía peligrosa (ADR)" }: { title?: string }) {
  return (
    <span
      title={title}
      aria-label={title}
      className="inline-flex h-5 w-5 rotate-45 items-center justify-center border-2 border-orange-700 bg-orange-500"
    >
      <span className="-rotate-45 font-mono text-[9px] font-bold leading-none text-orange-950">ADR</span>
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <label className="block" htmlFor={htmlFor}>
      <span className="micro-label">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && !error ? <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{hint}</p> : null}
      {error ? (
        <p role="alert" className="mt-1 text-xs font-medium text-signal-danger dark:text-red-300">
          {error}
        </p>
      ) : null}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`field num ${props.className ?? ""}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`field ${props.className ?? ""}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`field ${props.className ?? ""}`} />;
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`surface p-4 sm:p-5 ${className}`}>{children}</section>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-col gap-3 border-b border-ink-200 pb-4 dark:border-ink-800 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="micro-label">{eyebrow}</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink-900 dark:text-ink-50">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-3xl text-sm text-ink-600 dark:text-ink-300">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="surface flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-sm font-semibold text-ink-800 dark:text-ink-100">{title}</p>
      <p className="max-w-md text-sm text-ink-500 dark:text-ink-400">{description}</p>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "No se pudo completar la operación";
  return (
    <div
      role="alert"
      className="surface flex flex-col gap-2 border-signal-danger/40 bg-signal-danger/5 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-ink-800 dark:text-ink-100">{message}</p>
      {onRetry ? (
        <Button onClick={onRetry} variant="quiet">
          Reintentar
        </Button>
      ) : null}
    </div>
  );
}

export function Spinner({ label = "Cargando" }: { label?: string }) {
  return (
    <p role="status" className="py-8 text-center text-sm text-ink-500 dark:text-ink-400">
      {label}…
    </p>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 sm:p-8">
      <div role="dialog" aria-modal="true" aria-label={title} className="surface w-full max-w-2xl p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h2>
          <Button onClick={onClose} aria-label="Cerrar">
            Cerrar
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Acento de color de una `StatCard`: borde superior + fondo del icono. */
export type Accent = "brand" | "teal" | "violet" | "amber";

const ACCENT_CLASSES: Record<Accent, { border: string; iconBg: string; iconText: string }> = {
  brand: {
    // dark:border-t-brand-400 contrarresta el dark:border-ink-800 de .surface
    border: "border-t-brand-500 dark:border-t-brand-400",
    iconBg: "bg-brand-50 dark:bg-brand-950/60",
    iconText: "text-brand-600 dark:text-brand-300",
  },
  teal: {
    border: "border-t-teal-500 dark:border-t-teal-400",
    iconBg: "bg-teal-50 dark:bg-teal-950/40",
    iconText: "text-teal-600 dark:text-teal-300",
  },
  violet: {
    border: "border-t-violet-500 dark:border-t-violet-400",
    iconBg: "bg-violet-50 dark:bg-violet-950/40",
    iconText: "text-violet-600 dark:text-violet-300",
  },
  amber: {
    border: "border-t-amber-500 dark:border-t-amber-400",
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconText: "text-amber-600 dark:text-amber-300",
  },
};

/**
 * Tarjeta de indicador del Dashboard: borde superior de color + icono en
 * círculo + cifra grande + etiqueta. El color no decora: agrupa visualmente
 * las cuatro cifras clave para poder leerlas de un vistazo al entrar.
 */
export function StatCard({
  accent,
  icon,
  value,
  label,
  hint,
}: {
  accent: Accent;
  icon: ReactNode;
  value: ReactNode;
  label: string;
  hint?: string;
}) {
  const classes = ACCENT_CLASSES[accent];
  return (
    <div className={`surface border-t-[3px] p-4 ${classes.border}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="num text-2xl font-bold text-ink-900 dark:text-ink-50">{value}</p>
          <p className="micro-label mt-1">{label}</p>
          {hint ? <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">{hint}</p> : null}
        </div>
        <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${classes.iconBg} ${classes.iconText}`}>
          <span className="h-5 w-5">{icon}</span>
        </span>
      </div>
    </div>
  );
}

/** Cuadro de aviso con tono normativo (retención, bloqueo, ADR…). */
export function Notice({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  const map: Record<Tone, string> = {
    ok: "border-signal-ok/30 bg-signal-ok/5",
    warn: "border-signal-warn/40 bg-signal-warn/5",
    danger: "border-signal-danger/40 bg-signal-danger/5",
    neutral: "border-ink-200 bg-ink-50 dark:border-ink-800 dark:bg-ink-900",
  };
  return <div className={`rounded-lg border p-3 text-sm text-ink-700 dark:text-ink-200 ${map[tone]}`}>{children}</div>;
}
